import { useMemo, useState } from 'react';
import { Loader2, LockKeyhole, ShieldCheck, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Button, BrandLinkRow } from './ui';
import { ApiError, fetchWithTimeout, getAuthToken, getHeaders } from '../services/api';
import packageFavicon from '../logo/favicon.png';

interface PasswordRedirectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saasApiUrl?: string;
}

const PasswordRedirectModal = ({ open, onOpenChange, saasApiUrl }: PasswordRedirectModalProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const apiBaseUrl = useMemo(() => {
    if (!saasApiUrl) {
      return '';
    }

    const normalized = saasApiUrl.replace(/\/$/, '');
    return normalized.endsWith('/api') ? normalized : `${normalized}/api`;
  }, [saasApiUrl]);

  const buildUrl = (redirectUrl: string, magicToken?: string) => {
    if (redirectUrl) {
      return redirectUrl;
    }

    if (magicToken) {
      const fallbackUrl = new URL('/auth/sso-auto-connect', 'https://iam.ollaid.com');
      fallbackUrl.searchParams.set('magic_token', magicToken);
      return fallbackUrl.toString();
    }

    return '';
  };

  const handleRedirect = async () => {
    if (loading) return;
    setLoading(true);
    setErrorMessage(null);

    try {
      const authToken = getAuthToken();
      if (!authToken) {
        throw new Error('Session Native SSO introuvable');
      }

      if (!apiBaseUrl) {
        throw new Error('saasApiUrl non configurée');
      }

      const result = await fetchWithTimeout<{
        success: boolean;
        message?: string;
        magic_token?: string;
        sso_url?: string;
        redirect_url?: string;
      }>(
        `${apiBaseUrl}/sso/password-link`,
        {
          method: 'POST',
          headers: getHeaders(authToken || undefined, true),
        },
        20000
      );

      if (!result.success) {
        throw new Error(result.message || 'Impossible de générer le lien');
      }

      const redirectUrl = buildUrl(
        typeof result.redirect_url === 'string' && result.redirect_url.length > 0
          ? result.redirect_url
          : typeof result.sso_url === 'string' && result.sso_url.length > 0
            ? result.sso_url
            : '',
        result.magic_token
      );

      if (!redirectUrl) {
        throw new Error('URL de redirection manquante');
      }

      window.open(redirectUrl, '_blank', 'noopener,noreferrer');
      onOpenChange(false);
      setConfirmOpen(false);
    } catch (error: any) {
      const message = error instanceof ApiError
        ? error.message
        : error?.message || 'Impossible de lancer le flux de mot de passe';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent style={{ maxWidth: '28rem', borderRadius: '16px', background: '#fff' }}>
          <DialogHeader>
            <BrandLinkRow
              icon={<LockKeyhole size={18} />}
              faviconSrc={packageFavicon}
              faviconAlt="Ollaid SSO"
              iconBg="rgba(232, 67, 10, 0.1)"
              iconColor="#e8430a"
            />
            <DialogTitle className="text-center">Mot de passe géré par OLLAID SSO</DialogTitle>
            <DialogDescription className="text-center">
              Ce bouton sert à tester la redirection vers IAM et l'ouverture du modal de changement de mot de passe.
            </DialogDescription>
          </DialogHeader>

          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, background: '#f8fafc', color: '#475569', fontSize: 13, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <ShieldCheck size={16} color="#e8430a" />
              <span>Le SaaS agit comme relais. Le backend SaaS appelle IAM côté serveur, puis renvoie l'URL de redirection.</span>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <ExternalLink size={16} color="#e8430a" />
              <span>Une fenêtre IAM va s'ouvrir avec auto-connexion temporaire, sans appel direct du navigateur vers IAM.</span>
            </div>
          </div>

          <DialogFooter style={{ gap: 12 }}>
            <Button variant="outline" onClick={() => onOpenChange(false)} style={{ flex: 1 }} disabled={loading}>
              Fermer
            </Button>
            <Button onClick={() => setConfirmOpen(true)} disabled={loading} style={{ flex: 1 }}>
              {loading ? (
                <>
                  <Loader2 size={16} style={{ marginRight: 8, animation: 'spin 1s linear infinite' }} />
                  Préparation...
                </>
              ) : (
                'Changer mon mot de passe'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={(nextOpen) => {
        if (!loading) {
          setConfirmOpen(nextOpen);
        }
      }}>
        <DialogContent style={{ maxWidth: '26rem', borderRadius: '16px', background: '#fff' }}>
          <DialogHeader>
            <DialogTitle className="text-center">Êtes-vous sûr de rediriger vers le SSO ?</DialogTitle>
            <DialogDescription className="text-center">
              Cette action ouvre IAM dans un nouvel onglet et prépare l'auto-connexion pour tester le changement de mot de passe.
            </DialogDescription>
          </DialogHeader>
          {errorMessage && (
            <div style={{ border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', borderRadius: 12, padding: 12, fontSize: 13 }}>
              {errorMessage}
            </div>
          )}
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '8px 0 4px' }}>
              <Loader2 size={26} style={{ animation: 'spin 1s linear infinite', color: '#e8430a' }} />
              <div style={{ fontSize: 13, color: '#475569', textAlign: 'center' }}>
                Création de la session en cours...
              </div>
            </div>
          )}
          <DialogFooter style={{ gap: 12 }}>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} style={{ flex: 1 }} disabled={loading}>
              Annuler
            </Button>
            <Button onClick={handleRedirect} style={{ flex: 1 }} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={16} style={{ marginRight: 8, animation: 'spin 1s linear infinite' }} />
                  Confirmer...
                </>
              ) : (
                'Confirmer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PasswordRedirectModal;
